import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { of, Observable } from 'rxjs';
import { MMIconsModule, MmToastModule, TooltipModule } from 'mm-ui';
import { NgScrollbarModule } from 'ngx-scrollbar';

import { CreatePageService } from '../create-page/create-page.service';
import { ContentService } from '../content.service';
import { AccessProfileService } from '../../../access-profile/access-profile.service';
import { MediaService } from '../../../media/media.service';
import { ListItemPickerComponent } from 'projects/f-cms/src/app/shared/list-item-picker/list-item-picker.component';
import { PermissionMenuService } from './permisison-menu.service';

/**
 * Núcleo de permissões (visualização, edição, segregação) compartilhado entre
 * a sidebar `mm-permission-menu` e telas embarcadas (ex.: publicação no new-edit layout).
 */
@Component({
  selector: 'mm-permission-menu-content',
  standalone: true,
  templateUrl: './permission-menu-content.component.html',
  styleUrls: ['./permission-menu-content.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    MMIconsModule,
    TooltipModule,
    MmToastModule,
    NgScrollbarModule,
    ListItemPickerComponent
  ],
  providers: [AccessProfileService, MediaService]
})
export class PermissionMenuContentComponent implements OnInit, OnChanges {
  /** `sidebar` = scroll vertical (menu flutuante); `embedded` = duas colunas (layout new-edit / publicação). */
  @Input() viewMode: 'sidebar' | 'embedded' = 'sidebar';

  @Input('homepageId') homepageId: string;
  @Input('pageId') pageId: string;
  @Input() type: string;
  @Input() config: unknown;
  @Input() id: string;
  @Input('menu-id') menuId: string;

  @Output('updateList') updateListEmitter = new EventEmitter();

  permissionAclRules: any;
  permissionViewControl = new UntypedFormControl(null);
  permissionEditControl = new UntypedFormControl(null);
  itemSegregated = false;
  toastConfig = {
    position: 'top' as const,
    side: 'left' as const,
    noMargin: true
  };
  isHome = false;
  aclId: string;
  readGroups: unknown[] = [];
  writeGroups: unknown[] = [];
  writeGroupsList: unknown[] = [];
  readGroupsList: unknown[] = [];
  acl: any;
  areaText: string;
  closeMenu = false;
  itemName: string;
  showToast = false;

  constructor(
    private contentService: ContentService,
    private pageService: CreatePageService,
    private mediaService: MediaService,
    private accessProfileService: AccessProfileService,
    private permissionMenuService: PermissionMenuService,
    private translateService: TranslateService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.verifyIfIsHome();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['menuId'] && changes['menuId'].previousValue && changes['menuId'].currentValue != changes['menuId'].previousValue) {
      this.closeMenu = true;
    } else {
      this.closeMenu = false;
      this.itemName = '';
    }
  }

  verifyIfIsHome(): void {
    let itemConfig$: Observable<unknown> | undefined;

    switch (this.type) {
      case 'page':
        itemConfig$ = this.pageService.getPage(this.id);
        break;
      case 'homepage':
        itemConfig$ = this.contentService.getHomepageByIdEdit(this.id);
        break;
      case 'banner':
        itemConfig$ = of(this.config);
        break;
      case 'widget':
        itemConfig$ = of(this.config);
        break;
      case 'widgetSidebar':
        itemConfig$ = of(this.config);
        break;
      case 'media': {
        const inheritAcl = false;
        const base = this.config && typeof this.config === 'object' ? (this.config as Record<string, unknown>) : {};
        itemConfig$ = of({ ...base, inheritAcl } as { inheritAcl: boolean; id: string });
        break;
      }
      default:
        break;
    }

    if (itemConfig$) {
      itemConfig$.subscribe((config) => this.configItemPermission(config));
    }
  }

  configItemPermission(config: any): void {
    this.acl = config?.acl;
    this.aclId = this.acl?.id;
    this.readGroups = this.acl?.readGroups;
    this.writeGroups = this.acl?.writeGroups;
    this.readGroupsList = this.acl?.read_groups || [];
    this.writeGroupsList = this.acl?.write_groups || [];
    this.permissionAclRules = { ...config?.acl, ...{ inheritAcl: config.inheritAcl, canSegregateAcl: config.canSegregateAcl, canEditAcl: config.canEditAcl, itemType: (this.type ?? '').toUpperCase(), itemId: config.id } };
    this.itemName = config.title;
    switch (this.type) {
      case 'page':
        this.isHome = false;
        this.areaText = this.translateService.instant('cms.content.menu.thisPage');
        if (config.inheritAcl) {
          this.itemSegregated = false;
        } else {
          this.itemSegregated = true;
        }
        break;
      case 'homepage':
        this.isHome = true;
        this.areaText = this.translateService.instant('cms.content.menu.thisPage');
        if (this.acl?.defaultAcl) {
          this.itemSegregated = false;
        } else {
          this.itemSegregated = true;
        }
        break;
      case 'banner':
        this.isHome = false;
        this.areaText = this.translateService.instant('cms.content.menu.thisBanner');
        this.itemSegregated = !config?.inheritAcl;
        break;
      case 'widget':
        this.isHome = false;
        this.areaText = this.translateService.instant('cms.content.menu.thisWidget');
        this.itemSegregated = !config?.inheritAcl;
        break;
      case 'widgetSidebar':
        this.isHome = false;
        this.areaText = this.translateService.instant('cms.content.menu.thisWidget');
        this.itemSegregated = !config?.inheritAcl;
        break;
      case 'media':
        this.isHome = false;
        this.areaText = this.translateService.instant('cms.content.menu.thisMedia');
        this.itemSegregated = !config?.inheritAcl;
        break;
      default:
        break;
    }
  }

  segregateItem(): void {
    let segregateItem$: Observable<unknown> | undefined;

    switch (this.type) {
      case 'homepage':
        segregateItem$ = this.contentService.segregateHomepageArea(this.id, this.id);
        break;
      case 'page':
        segregateItem$ = this.contentService.segregateArea(this.id, this.menuId, this.id);
        break;
      case 'banner':
        segregateItem$ = of({ ...this.acl, inheritAcl: false });
        break;
      case 'widget':
        segregateItem$ = of({ ...this.acl, inheritAcl: false });
        break;
      case 'widgetSidebar':
        segregateItem$ = of({ ...this.acl, inheritAcl: false });
        break;
      default:
        break;
    }

    if (segregateItem$) {
      segregateItem$.subscribe({
        next: (resp) => this.segregateItemSuccess(resp),
        error: () =>
          this.setMessage(
            'danger',
            this.translateService.instant('cms.content.menu.segregateError'),
            this.translateService.instant('cms.content.menu.segregateItem')
          )
      });
    }
  }

  segregateItemSuccess(resp: any): void {
    this.itemSegregated = !this.itemSegregated;
    this.acl = resp;
    this.aclId = resp.id ? resp.id : resp.acl?.id;
    this.readGroups = resp.readGroups ? resp.readGroups : resp.acl?.readGroups ? resp.acl.readGroups : [];
    this.writeGroups = resp.writeGroups ? resp.writeGroups : resp.acl?.writeGroups ? resp.aclwriteGroups : [];
    this.readGroupsList = resp.acl && resp.acl.read_groups ? resp.acl.read_groups : [];
    this.writeGroupsList = resp.acl && resp.acl.write_groups ? resp.acl.write_groups : [];

    switch (this.type) {
      case 'homepage':
        this.setMessage(
          'success',
          this.translateService.instant('cms.content.menu.segregateHomeSuccess'),
          this.translateService.instant('cms.content.menu.segregateHome')
        );
        break;
      case 'page':
        this.setMessage(
          'success',
          this.translateService.instant('cms.content.menu.segregatePageSuccess'),
          this.translateService.instant('cms.content.menu.segregatePage')
        );
        break;
      case 'banner':
        this.setMessage(
          'success',
          this.translateService.instant('cms.content.menu.segregateBannerSuccess'),
          this.translateService.instant('cms.content.menu.segregateBanner')
        );
        break;
      case 'widget':
        this.setMessage(
          'success',
          this.translateService.instant('cms.content.menu.segregateWidgetSuccess'),
          this.translateService.instant('cms.content.mnenu.segregateWidget')
        );
        break;
      case 'widgetSidebar':
        this.setMessage(
          'success',
          this.translateService.instant('cms.content.menu.segregateWidgetSuccess'),
          this.translateService.instant('cms.content.mnenu.segregateWidget')
        );
        break;
      default:
        break;
    }

    this.updateList();
  }

  removeSegregateItem(): void {
    let removeSegregate$: Observable<unknown> | undefined;

    switch (this.type) {
      case 'homepage':
        removeSegregate$ = this.contentService.removeSegregateHomepage(this.id);
        break;
      case 'page':
        removeSegregate$ = this.contentService.removeSegregatePage(this.id, this.menuId);
        break;
      case 'banner':
        removeSegregate$ = of({ ...this.acl, inheritAcl: true });
        break;
      case 'widget':
        removeSegregate$ = of({ ...this.acl, inheritAcl: true });
        break;
      case 'widgetSidebar':
        removeSegregate$ = of({ ...this.acl, inheritAcl: true });
        break;
    }

    if (removeSegregate$) {
      removeSegregate$.subscribe({
        next: (res) => this.removeSegregateSuccess(res),
        error: () =>
          this.setMessage(
            'danger',
            this.translateService.instant('cms.content.menu.segregateRemoveError'),
            this.translateService.instant('cms.content.menu.segregateRemove')
          )
      });
    }
  }

  removeSegregateSuccess(res: any): void {
    this.acl = res;
    this.aclId = res.id;
    this.itemSegregated = false;
    this.readGroups = res.readGroups ? res.readGroups : res.acl.readGroups;
    this.writeGroups = res.writeGroups ? res.writeGroups : res.acl.writeGroups;
    this.readGroupsList = res.acl && res.acl.read_groups ? res.acl.read_groups : [];
    this.writeGroupsList = res.acl && res.acl.write_groups ? res.acl.write_groups : [];

    this.setMessage(
      'success',
      this.translateService.instant('cms.content.menu.segregateRemoveSuccess'),
      this.translateService.instant('cms.content.menu.segregateRemove')
    );
    this.closeMenu = true;
  }

  setMessage(severity: string, summary: string, detail: string): void {
    this.messageService.add({ severity, summary, detail });
    this.updateList();
  }

  updateList(): void {
    this.updateListEmitter.emit(this.acl);
    this.permissionMenuService.updateList(this.acl);
  }

  searchGroups(type: string, e: string): Observable<unknown> {
    let permissionType: unknown[] = [];
    if (type == 'read') {
      permissionType = (this.readGroups as unknown[]) || [];
    } else if (type == 'write') {
      permissionType = (this.writeGroups as unknown[]) || [];
    }
    return new Observable((observer) => {
      this.accessProfileService.getGroups({ name: e, unpaged: true }).subscribe({
        next: (resp) => {
          const content = (resp as { content: { id: string; name: string }[] }).content;
          observer.next(
            content
              .filter((res) => (permissionType as string[]).indexOf(res.id) <= -1)
              .map((x) => ({ name: x.name, id: x.id }))
          );
        },
        error: () => {
          observer.next(null);
        }
      });
    });
  }

  populateList(event: any, typePermission: string, type: 'added' | 'removed' | 'onInit'): void {
    if (typePermission == 'read') {
      const read: string[] = [];
      if (event && event.length > 0) {
        event.forEach((element) => {
          read.push((element as { id?: string; clientId?: string })?.id ? (element as { id: string }).id : (element as { clientId: string }).clientId);
          this.readGroups = read;
          if (this.acl) {
            this.acl.readGroups = read;
          }
        });
      } else {
        this.readGroups = [];
      }
    } else {
      const write: string[] = [];
      if (event && event.length > 0) {
        event.forEach((element) => {
          write.push((element as { clientId?: string; id?: string })?.clientId ? (element as { clientId: string }).clientId : (element as { id: string }).id);
          this.writeGroups = write;
          if (this.acl) {
            this.acl.writeGroups = write;
          }
        });
      } else {
        this.writeGroups = [];
      }
    }
    let editGroups$: Observable<unknown>;
    let itemType = (this.type ?? '').toUpperCase();
    if (this.type && this.type.toUpperCase() == 'WIDGETSIDEBAR') {
      itemType = 'WIDGET';
    }
    switch (this.type) {
      case 'media': {
        const readIds = (this.readGroups ?? []) as string[];
        if (this.aclId) {
          editGroups$ = this.mediaService.editAcl(this.aclId, readIds);
        } else {
          editGroups$ = this.mediaService.createAcl(this.id, readIds);
        }
        break;
      }
      default:
        editGroups$ = this.contentService.editAcl(this.aclId, {
          readGroups: this.readGroups,
          writeGroups: this.writeGroups,
          itemType,
          itemId: (this.permissionAclRules as { itemId: string }).itemId,
          menuId: this.menuId
        } as { readGroups: string[]; writeGroups: string[]; itemType: string; itemId: string; menuId: string });
        break;
    }
    const messageText = this.translateService.instant(`content.permission.${type}.${this.type}`);
    editGroups$.subscribe({
      next: () => this.setMessage('success', messageText, this.translateService.instant('cms.content.menu.itemChange')),
      error: () =>
        this.setMessage('danger', this.translateService.instant('cms.content.menu.itemChangeError'), this.translateService.instant('cms.content.menu.itemChange'))
    });
  }
}
